import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Layout,
  Button,
  Row,
  Col,
  Form,
  Input,
  Switch,
  Modal,
  Alert,
} from "antd";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { ShowToast, Severty } from "../helper/toast";
import { AuthContext } from "../context/AuthContext";
import Loader from "../components/Loader";
import encryptDecrypt from "../helper/encryptDecrypt";

const { Content } = Layout;

const REMEMBER_ME_KEY = "rememberMe";
const REMEMBER_EMAIL_KEY = "ykmCe2AYEFTHobn";
const REMEMBER_PASSWORD_KEY = "ouUsippetc8S4Ry";

const clearRememberMeStorage = () => {
  localStorage.removeItem(REMEMBER_ME_KEY);
  localStorage.removeItem(REMEMBER_EMAIL_KEY);
  localStorage.removeItem(REMEMBER_PASSWORD_KEY);
};

const SignIn = () => {
  const { setIsLoggedIn, setSession, setUserProfile } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [otpModal, setOtpModal] = useState(false); // Forgot-password OTP modal
  const [loginOtpModal, setLoginOtpModal] = useState(false); // Admin-login OTP modal
  const [resetModal, setResetModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [visible, setVisible] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [pendingRemember, setPendingRemember] = useState(false);
  const [pendingLoginCredentials, setPendingLoginCredentials] = useState(null); // { email, password, apiEmail }
  const [loginOtpRetryAfter, setLoginOtpRetryAfter] = useState(0);
  const { request } = useRequest();
  const navigate = useNavigate();
  const location = useLocation();
  const [postResetNotice, setPostResetNotice] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accountStatus = params.get("account");
    if (accountStatus === "inactive") {
      ShowToast(
        "Your account is inactive. Please contact an administrator to activate it.",
        Severty.ERROR,
      );
    } else if (accountStatus === "deleted") {
      ShowToast(
        "Your account is no longer available. Please contact support if this seems incorrect.",
        Severty.ERROR,
      );
    } else {
      return;
    }
    navigate("/login", { replace: true });
  }, [location.search, navigate]);

  const [form] = Form.useForm();
  const [form1] = Form.useForm();
  const [form2] = Form.useForm();
  const [form3] = Form.useForm();
  const [loginOtpForm] = Form.useForm();

  // ----------- LOGIN HANDLER -----------
  const onFinish = (values) => {
    const { email, password } = values;
    const identifier = email?.trim();
    if (!identifier) return ShowToast("Please enter your email", Severty.ERROR);

    const payload = {
      emailAddress: identifier,
      setPassword: password,
    };

    setLoading(true);
    request({
      url: apiPath.adminLogin,
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        setLoading(false);
        setPendingRemember(rememberMe);
        setPendingLoginCredentials({
          email: values.email,
          password: values.password,
          apiEmail: data?.email || identifier,
        });
        const cooldown = Number(data?.resendCooldownSeconds);
        setLoginOtpRetryAfter(Number.isFinite(cooldown) ? cooldown : 30);
        loginOtpForm.resetFields(["otp"]);
        setLoginOtpModal(true);
        ShowToast(
          data?.message || "OTP sent to your email. Please verify to continue.",
          Severty.SUCCESS
        );
      },
      onError: (error) => {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Invalid email or password";
        ShowToast(msg, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  const completeLoginAfterOtp = async (loginData) => {
    if (!loginData?.token || !loginData?.user) return;

    if (pendingRemember) {
      const emailToEncrypt = pendingLoginCredentials?.email;
      const passwordToEncrypt = pendingLoginCredentials?.password;
      if (!emailToEncrypt || !passwordToEncrypt) {
        clearRememberMeStorage();
      } else {
        const emailEncrypt = encryptDecrypt.encryptEmail(emailToEncrypt);
        const passwordEncrypt = encryptDecrypt.encryptPassword(passwordToEncrypt);
        localStorage.setItem(REMEMBER_ME_KEY, "true");
        localStorage.setItem(REMEMBER_EMAIL_KEY, emailEncrypt);
        localStorage.setItem(REMEMBER_PASSWORD_KEY, passwordEncrypt);
      }
    } else {
      clearRememberMeStorage();
    }

    localStorage.setItem("token", loginData.token);
    localStorage.setItem("userProfile", JSON.stringify(loginData.user));
    setSession({ token: loginData.token });
    setUserProfile(loginData.user);

    // Hydrate full profile (including profilePic) before first dashboard render when possible.
    let nextProfile = loginData.user;
    try {
      const profileData = await request({
        url: apiPath.authProfile,
        method: "GET",
      });
      const apiUser = profileData?.user;
      if (apiUser) {
        nextProfile = {
          ...loginData.user,
          ...apiUser,
          id: apiUser._id || apiUser.id || loginData.user.id,
        };
        localStorage.setItem("userProfile", JSON.stringify(nextProfile));
        setUserProfile(nextProfile);
      }
    } catch {
      // Keep login non-blocking even if profile fetch fails.
    } finally {
      setIsLoggedIn(true);
      ShowToast("Login successful", Severty.SUCCESS);
      navigate("/dashboard");
    }

    setPendingRemember(false);
    setPendingLoginCredentials(null);
    setLoginOtpRetryAfter(0);
    setLoginOtpModal(false);
  };

  const handleVerifyLoginOtp = (values) => {
    if (!pendingLoginCredentials?.apiEmail) {
      ShowToast("Session expired. Please login again.", Severty.ERROR);
      setLoginOtpModal(false);
      return;
    }
    const otp = (values?.otp ?? "").toString().trim();
    setLoading(true);
    request({
      url: apiPath.adminLoginVerifyOtp,
      method: "POST",
      data: {
        emailAddress: pendingLoginCredentials.apiEmail,
        otp,
      },
      onSuccess: (data) => {
        setLoading(false);
        completeLoginAfterOtp(data);
      },
      onError: (error) => {
        setLoading(false);
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "OTP verification failed";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  const handleResendLoginOtp = () => {
    const emailAddress = pendingLoginCredentials?.apiEmail;
    const setPassword = pendingLoginCredentials?.password;
    if (!emailAddress || !setPassword) {
      ShowToast("Session expired. Please login again.", Severty.ERROR);
      setLoginOtpModal(false);
      return;
    }

    setLoading(true);
    request({
      url: apiPath.adminLogin,
      method: "POST",
      data: {
        emailAddress,
        setPassword,
      },
      onSuccess: (data) => {
        setLoading(false);
        const cooldown = Number(data?.resendCooldownSeconds);
        setLoginOtpRetryAfter(Number.isFinite(cooldown) ? cooldown : 30);
        loginOtpForm.resetFields(["otp"]);
        ShowToast(data?.message || "OTP resent successfully.", Severty.SUCCESS);
      },
      onError: (error) => {
        setLoading(false);
        const retryAfterSeconds = Number(error?.response?.data?.retryAfterSeconds);
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
          setLoginOtpRetryAfter(retryAfterSeconds);
        }
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to resend OTP";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  // ----------- FORGOT PASSWORD HANDLERS -----------
  const handleForgotPassword = () => setVisible(true);

  const handleResetPassword = (email) => {
    setForgotSubmitting(true);
    request({
      url: apiPath.authForgotPassword,
      method: "POST",
      data: { email: email.email },
      onSuccess: (data) => {
        setForgotSubmitting(false);
        setVisible(false);
        form1.resetFields();
        ShowToast(
          "Check your inbox and click the reset URL to set a new password.",
          Severty.SUCCESS
        );
        // OTP reset is handled by clicking the reset URL from the email.
        setOtpModal(false);
        setResetModal(false);
      },
      onError: (error) => {
        setForgotSubmitting(false);
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send reset email";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  const handleVerifyOTP = (values) => {
    if (!resetEmail) {
      ShowToast("Please start from Forgot Password and enter your email", Severty.ERROR);
      setOtpModal(false);
      setVisible(true);
      return;
    }

    request({
      url: apiPath.authVerifyOtp,
      method: "POST",
      data: { email: resetEmail, otp: values.otp },
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setOtpModal(false);
        setResetModal(true);
        form2.resetFields();
      },
      onError: (error) => {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "OTP verification failed";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  const handleReset = (values) => {
    if (!resetEmail) {
      ShowToast("Please start from Forgot Password and verify OTP", Severty.ERROR);
      setResetModal(false);
      setVisible(true);
      return;
    }

    request({
      url: apiPath.authResetPassword,
      method: "POST",
      data: {
        email: resetEmail,
        password: values.newPassword,
      },
      onSuccess: (data) => {
        ShowToast(data.message, Severty.SUCCESS);
        setResetModal(false);
        setResetEmail("");
        form3.resetFields();
      },
      onError: (error) => {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Password reset failed";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    const wantsRemember = localStorage.getItem(REMEMBER_ME_KEY) === "true";
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY);
    const savedPassword = localStorage.getItem(REMEMBER_PASSWORD_KEY);
    if (!wantsRemember || !savedEmail || !savedPassword) {
      return;
    }
    try {
      const originalEmail = encryptDecrypt.decryptEmail(savedEmail);
      const originalPassword = encryptDecrypt.decryptPassword(savedPassword);
      if (originalEmail && originalPassword) {
        form.setFieldsValue({ email: originalEmail, password: originalPassword });
        setRememberMe(true);
      } else {
        clearRememberMeStorage();
        setRememberMe(false);
      }
    } catch {
      clearRememberMeStorage();
      setRememberMe(false);
    }
  }, [form]);

  useEffect(() => {
    const st = location.state;
    if (!st?.fromPasswordReset) return;
    if (st.resetEmail) {
      form.setFieldsValue({ email: st.resetEmail });
    }
    setPostResetNotice(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, form]);

  useEffect(() => {
    if (!loginOtpRetryAfter || loginOtpRetryAfter <= 0) return undefined;
    const timer = setInterval(() => {
      setLoginOtpRetryAfter((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [loginOtpRetryAfter]);

  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      clearRememberMeStorage();
    }
  };

  return (
    <>
      {loading && <Loader />}
      <Layout className="layout-default layout-signin">
        <Row className="align-items-center layout-signin-rowsss">
          <Col span={24}>
            <Content className="signin">
              <div className="signin-box">
                <div className="signup-logo">
                  <h2>Log In</h2>
                  <h4>Admin access</h4>

                </div>

                {postResetNotice ? (
                  <Alert
                    type="success"
                    showIcon
                    closable
                    message="Password updated"
                    description="Your password was reset. Sign in below with the same email you used for Forgot password and your new password."
                    style={{ marginBottom: 20 }}
                    onClose={() => setPostResetNotice(false)}
                  />
                ) : null}

                <Form
                  form={form}
                  onFinish={onFinish}
                  layout="vertical"
                  className="row-col"
                >
                  <Form.Item
                    label="Email"
                    name="email"
                    normalize={(value) => value?.trim()}
                    rules={[
                      {
                        required: true,
                        message: "Please input your email!",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email!",
                      },
                    ]}
                  >
                    <Input placeholder="Enter Email" />
                  </Form.Item>

                  <Form.Item
                    label="Password"
                    name="password"
                    normalize={(value) => value?.trim()}
                    rules={[
                      { required: true, message: "Please input your password!" },
                      { max: 100, message: "Password cannot exceed 100 characters" },
                    ]}
                  >
                    <Input.Password placeholder="Password" />
                  </Form.Item>

                  <div className="forgot-pass">
                    <div className="remember-me-row">
                      <Switch checked={rememberMe} onChange={handleRememberMeChange} />
                      <span className="remember-me-label">Remember me</span>
                    </div>
                    <div className="forgate-btn" onClick={handleForgotPassword}>
                      <a>Forgot Password?</a>
                    </div>
                  </div>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      style={{ width: "100%" }}
                    >
             LOGIN 
                    </Button>
                  </Form.Item>
                  {/* <div className="signin-text" onClick={() => navigate("/register")}>
                    Don’t have an account? <span>Sign Up</span>
                  </div> */}
                </Form>
              </div>
            </Content>
          </Col>
        </Row>

        {/* ------- Forgot Password Modal ------- */}
        <Modal
          open={visible}
          title="Forgot Password"
          onCancel={() => {
            setVisible(false);
            form1.resetFields();
          }}
          okText="Submit"
          cancelText="Cancel"
          okButtonProps={{
            form: "forget-password",
            htmlType: "submit",
            loading: forgotSubmitting,
          }}
        >
          <Form
            id="forget-password"
            form={form1}
            onFinish={handleResetPassword}
            layout="vertical"
          >
            <Form.Item
              label="Email"
              name="email"
              normalize={(value) => value?.trim()}
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input placeholder="Enter Email" />
            </Form.Item>
          </Form>
        </Modal>

        {/* ------- Admin Login OTP Modal ------- */}
        <Modal
          open={loginOtpModal}
          title="Admin Login OTP"
          onCancel={() => {
            setLoginOtpModal(false);
            setPendingRemember(false);
            setPendingLoginCredentials(null);
            setLoginOtpRetryAfter(0);
            loginOtpForm.resetFields();
          }}
          okButtonProps={{
            form: "admin-verify-otp",
            htmlType: "submit",
            loading: loading,
          }}
          footer={null}
        >
          <p style={{ marginBottom: 12 }}>
            OTP sent to: <strong>{pendingLoginCredentials?.email || "your email"}</strong>
          </p>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="link"
              onClick={handleResendLoginOtp}
              disabled={loading || loginOtpRetryAfter > 0}
              style={{ padding: 0, height: "auto" }}
            >
              {loginOtpRetryAfter > 0
                ? `Resend OTP in ${loginOtpRetryAfter}s`
                : "Resend OTP"}
            </Button>
          </div>
          <Form
            id="admin-verify-otp"
            form={loginOtpForm}
            onFinish={handleVerifyLoginOtp}
            layout="vertical"
          >
            <Form.Item
              label="OTP"
              name="otp"
              normalize={(value) => value?.toString().trim()}
              rules={[
                { required: true, message: "Please enter the OTP" },
                { pattern: /^\d{6}$/, message: "OTP must be 6 digits" },
              ]}
            >
              <Input
                placeholder="Enter 6-digit OTP"
                inputMode="numeric"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ width: "100%" }}>
                Verify OTP
              </Button>
            </Form.Item>
          </Form>
        </Modal>

      </Layout>
    </>
  );
};

export default SignIn;